import React from 'react';
import { Check, Search, LayoutGrid, Users, Mail, Zap, CheckCircle2, TrendingUp, Sparkles } from 'lucide-react';
import { SubscriptionPlan } from '../../../types/subscription.types';
import {
  PLAN_FEATURES,
  PARENT_TIER,
  PARENT_DISPLAY_NAME,
  getActiveSearchLabel,
} from '../SubscriptionPlans/SubscriptionPlans';

interface PlanPickerCardProps {
  plan: SubscriptionPlan;
  allPlans: SubscriptionPlan[];
  isSelected: boolean;
  ctaLabel: string;
  ctaBusy: boolean;
  trialEligible?: boolean;
  onSelect: (planId: number) => void;
  onCheckout: (planId: number) => void;
}

const PlanPickerCard: React.FC<PlanPickerCardProps> = ({
  plan,
  allPlans,
  isSelected,
  ctaLabel,
  ctaBusy,
  trialEligible = true,
  onSelect,
  onCheckout,
}) => {
  const showTrialFeePricing = trialEligible && !!plan.trial_fee_display;
  const isScale = plan.name === 'scale';
  const showPopularBadge = plan.badge_type === 'MOST_POPULAR';

  const parentTierName = PARENT_TIER[plan.name];
  const parentPlan = parentTierName ? allPlans.find((p) => p.name === parentTierName) : undefined;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(plan.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(plan.id);
      }}
      className={`relative flex h-full cursor-pointer flex-col rounded-2xl border-[1.5px] bg-white p-6 transition-all hover:-translate-y-0.5 ${
        isSelected ? 'border-primary shadow-[0_16px_38px_rgba(138,48,51,0.16)]' : 'border-gray-200 shadow-sm'
      }`}
    >
      {showPopularBadge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-accent px-3.5 py-1 text-[10.5px] font-bold uppercase tracking-wide text-white shadow-md">
          {isSelected ? 'Selected' : 'Most popular'}
        </span>
      )}

      <div className="mb-1.5 flex items-center gap-2.5">
        <span
          className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border-2 ${
            isSelected ? 'border-primary bg-primary' : 'border-gray-300 bg-white'
          }`}
        >
          <Check className={`h-2.5 w-2.5 text-white ${isSelected ? 'opacity-100' : 'opacity-0'}`} strokeWidth={3.2} />
        </span>
        <h3 className="text-lg font-bold text-secondary">{plan.display_name}</h3>
      </div>

      {showTrialFeePricing ? (
        <>
          <div className="mb-0.5 flex items-baseline gap-1">
            <span className="text-4xl font-extrabold tracking-tight text-secondary">{plan.trial_fee_display}</span>
            <span className="text-sm font-medium text-gray-500">for 5 days</span>
          </div>
          <div className="mb-4 text-xs text-gray-400">
            then {plan.price_display}/mo &middot; cancel anytime before
          </div>
        </>
      ) : (
        <>
          <div className="mb-0.5 flex items-baseline gap-1">
            <span className="text-4xl font-extrabold tracking-tight text-secondary">{plan.price_display}</span>
            <span className="text-sm font-medium text-gray-500">/mo</span>
          </div>
          <div className="mb-4 text-xs text-gray-400">Billed monthly</div>
        </>
      )}

      <div className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">Usage limits</div>
      <div className="mb-4 flex flex-col gap-2">
        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-secondary">
          <Search className="h-4 w-4 flex-none text-blue-600" />
          {plan.daily_searches} {plan.daily_searches === 1 ? 'search' : 'searches'} per day
        </div>
        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-secondary">
          <LayoutGrid className="h-4 w-4 flex-none text-secondary" />
          Up to {plan.max_results.toLocaleString()} results per search
        </div>
        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-secondary">
          <Users className="h-4 w-4 flex-none text-purple-600" />
          {plan.monthly_dm_unlocks.toLocaleString()} decision maker lookups /mo
        </div>
        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-secondary">
          <Mail className="h-4 w-4 flex-none text-map-green" />
          {plan.monthly_email_verifications.toLocaleString()} email verifications /mo
        </div>
        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-secondary">
          <Zap className="h-4 w-4 flex-none text-accent" />
          Prospects Website matching filter
        </div>
      </div>

      <div className="my-4 h-px bg-gray-100" />

      <div className="mb-5 flex-1">
        <div className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">Includes</div>
        {parentPlan ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 flex-none text-green-600" />
              <span className="text-xs font-bold text-green-700">All from {PARENT_DISPLAY_NAME[parentTierName]}</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 flex-none text-blue-600" />
              <span className="text-xs font-bold text-blue-700">
                {isScale ? '2x' : '3x'} higher usage than {PARENT_DISPLAY_NAME[parentTierName]}
              </span>
            </div>
            {PLAN_FEATURES.filter((f) => f.getAvailable(plan) && !f.getAvailable(parentPlan)).map((f) => (
              <div key={f.label} className="flex items-start gap-2 pt-0.5">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-none text-purple-500" />
                <span className="text-xs font-semibold text-purple-700">{f.label}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {PLAN_FEATURES.filter((f) => f.getAvailable(plan)).map((f) => (
              <div key={f.label} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-map-green" />
                <span className="text-xs text-gray-700">{f.label}</span>
              </div>
            ))}
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-map-green" />
              <span className="text-xs text-gray-700">{getActiveSearchLabel(plan)}</span>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={ctaBusy}
        onClick={(e) => {
          e.stopPropagation();
          onCheckout(plan.id);
        }}
        className={`w-full rounded-xl py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
          isSelected
            ? 'bg-primary text-white shadow-[0_8px_20px_rgba(138,48,51,0.22)] hover:bg-primary-hover'
            : 'border-[1.5px] border-gray-200 bg-white text-secondary hover:border-primary hover:text-primary'
        }`}
      >
        {isSelected && ctaBusy ? 'Redirecting…' : ctaLabel}
      </button>
    </div>
  );
};

export default PlanPickerCard;
